import { ThunkDispatch } from 'redux-thunk';
import {
  CLICK_ACTIONS,
  ONBOARDING_TYPE,
  REGISTRATION_STATUS,
  TABLE_SORT_DESC,
} from '../../utils/Constant';
import {
  ApprovalFilterType,
  ColumnHeaderType,
  LoggedUserType,
  OnboardingRequestType,
  UserType,
} from '../../utils/Types';
import {
  blankValueGetter,
  formattedDate,
  getCompletedClassNameGetter,
  getCompletedStatus,
  getKYCAction,
  getKYCActionText,
  getKYCStatus,
  getKYCStatusClassNameGetter,
  isAPIFailed,
  isAPISuccess,
  nameValueGetter,
} from '../../utils/Util';
import {
  resetUpdateKYCDocumentStatus,
  saveCommentNote,
  updateRegistrationStatus,
} from '../../features/onboardingSlice';
import { toast } from 'react-toastify';
import { STATE_ACCESS_ROLES } from '../../utils/RBAC';

export const REGISTRATION_APPROVAL_HEADER: ColumnHeaderType[] = [
  {
    name: 'Candidate Id',
    field: 'candidateUniqueId',
    sortable: true,
    showProfilePhoto: true,
  },
  {
    name: 'Candidate Name',
    field: 'name',
    sortable: true,
    toolTipAfter: 25,
    valueGetter: nameValueGetter,
  },
  {
    name: 'Pincode',
    field: 'pincode',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Mobile Number',
    field: 'mobileNumber',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Email Id',
    field: 'emailId',
    sortable: true,
    toolTipAfter: 20,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Registration Date',
    field: 'createdOn',
    sortable: true,
    valueGetter: (data: any, field?: string) => {
      return formattedDate(String(data[field as keyof any]));
    },
  },
  {
    name: 'Actions',
    field: 'userActivationStatus',
    sortable: true,
    clickable: true,
    action: CLICK_ACTIONS.APPROVE,
    value: 'Approve/Reject',
  },
];

export const KYC_VERIFICATION_HEADER: ColumnHeaderType[] = [
  {
    name: 'Candidate Id',
    field: 'candidateUniqueId',
    showProfilePhoto: true,
  },
  {
    name: 'Candidate Name',
    field: 'name',
    toolTipAfter: 25,
    valueGetter: nameValueGetter,
  },
  {
    name: 'Pincode',
    field: 'pincode',
    valueGetter: blankValueGetter,
  },
  {
    name: 'Mobile Number',
    field: 'mobileNumber',
    valueGetter: blankValueGetter,
  },
  {
    name: 'Reporting Manager',
    field: 'reportingManagerName',
    sortable: true,
    toolTipAfter: 25,
    valueGetter: blankValueGetter,
  },
  {
    name: 'KYC Status',
    field: 'kycStatus',
    sortable: true,
    valueGetter: getKYCStatus,
    classNameGetter: getKYCStatusClassNameGetter,
  },
  {
    name: 'Training Status',
    field: 'trainingStatus',
    sortable: true,
    valueGetter: getCompletedStatus,
    classNameGetter: getCompletedClassNameGetter,
  },
  {
    name: 'Test Status',
    field: 'testStatus',
    sortable: true,
    valueGetter: getCompletedStatus,
    classNameGetter: getCompletedClassNameGetter,
  },
  {
    name: 'Actions',
    field: 'kycStatus',
    sortable: true,
    clickable: true,
    valueGetter: getKYCActionText,
    actionGetter: getKYCAction,
  },
];

/**
 * Prepare request parameters to get candidate list from server
 * @param onboardingType
 * @param user
 * @param userDetails
 * @param selectedFilter
 * @param pageSize
 * @param currentPage
 * @returns { data, params }
 */
export const getCandidateRequest = (
  onboardingType: string,
  user: LoggedUserType,
  userDetails: UserType,
  selectedFilter: any,
  pageSize: number,
  currentPage: number
) => {
  const data: OnboardingRequestType = {
    internalEmployeeId: user.candidateId,
    status:
      onboardingType === ONBOARDING_TYPE.REGISTRATION_APPROVAL
        ? REGISTRATION_STATUS.PENDING
        : REGISTRATION_STATUS.APPROVED,
    filters: {},
  };
  if (
    STATE_ACCESS_ROLES.includes(user?.roleId) &&
    userDetails?.reportingZones?.length
  ) {
    data.filters.zone = Number(userDetails?.reportingZones[0]);
  }
  if (selectedFilter.name) data.filters.name = selectedFilter.name;
  if (selectedFilter.pincode) data.filters.pincode = selectedFilter.pincode;
  if (selectedFilter.candidateId)
    data.filters.candidateId = selectedFilter.candidateId;
  if (selectedFilter.state) data.filters.state = Number(selectedFilter.state);
  if (selectedFilter.location)
    data.filters.location = Number(selectedFilter.location);
  if (selectedFilter.district)
    data.filters.district = Number(selectedFilter.district);
  if (selectedFilter.zone) data.filters.zone = Number(selectedFilter.zone);

  const params = `?size=${pageSize}&page=${currentPage}&sort=${TABLE_SORT_DESC}`; //sort = AESC or DESC
  return { data, params };
};

/**
 * Get filters values form selected filter
 * @param approvalFilter
 * @returns filterObject
 */
export const getFilterObject = (approvalFilter: ApprovalFilterType) => {
  return {
    candidateId: approvalFilter.candidateId,
    name: approvalFilter.candidateName,
    state: approvalFilter.state?.value,
    location: approvalFilter.city?.value,
    pincode: approvalFilter.pincode,
    district: approvalFilter.district?.value,
    zone: approvalFilter.zone?.value,
  };
};
/**
 * This method call same method API to save the comment/log for the selected candidate.
 */
export const saveComment = (
  comment: string,
  candidateId: number | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  setShowLoader: (value: boolean) => void,
  getUserDetails: () => void,
  setComment: (value: string) => void,
  documentId?: number | undefined
) => {
  if (comment) {
    const data = {
      notesDescription: comment,
      module: 1, // 1 - REGISTRATION, 2 - TASK_ASSIGNMENT
      referenceId: candidateId, // for REGISTRATION - candidate_id, for TASK_ASSIGNMENT : task_assignment_id
      kycDocId: documentId, //for Kyc Document
    };
    setShowLoader(true);
    dispatch(saveCommentNote(data))
      ?.then((fulfilled: any) => {
        if (isAPIFailed(fulfilled?.payload)) {
          toast.error(fulfilled?.payload?.responseInformation.responseMessage);
        } else if (isAPISuccess(fulfilled?.payload)) {
          toast.success(
            fulfilled?.payload?.responseInformation.responseMessage
          );
        }
        setShowLoader(false);
        getUserDetails();
      })
      ?.catch(() => {
        toast.error('Comment add failed!');
        setShowLoader(false);
      });
    setComment('');
  }
};
/**
 * Update Approve and reject status of candidate.
 * @param action
 */
export const onActionPerform = (
  action: string,
  candidateId: number | undefined,
  loggedUser: LoggedUserType,
  comment: string,
  dispatch: ThunkDispatch<any, any, any>,
  setShowLoader: (value: boolean) => void,
  toggleApproveModal: (value: string) => void
) => {
  try {
    const data = {
      candidateId: candidateId,
      activationStatus: action === CLICK_ACTIONS.APPROVE ? 0 : 1,
      approverId: loggedUser.candidateId,
      comments: comment,
    };
    setShowLoader(true);
    dispatch(updateRegistrationStatus(data))
      ?.then((fulfilled: any) => {
        setShowLoader(false);
        if (isAPIFailed(fulfilled?.payload)) {
          toast.error(fulfilled?.payload?.responseInformation.responseMessage);
        } else if (isAPISuccess(fulfilled?.payload)) {
          toast.success(
            fulfilled?.payload?.responseInformation.responseMessage
          );
          toggleApproveModal(CLICK_ACTIONS.REFRESH_LIST);
        }
      })
      ?.catch(() => {
        setShowLoader(false);
        if (action === CLICK_ACTIONS.APPROVE) {
          toast.error('Candidate approve failed!');
        } else if (action === CLICK_ACTIONS.REJECT) {
          toast.error('Candidate rejection failed!');
        }
      });
  } catch (e) {}
};

export const onStatusUpdate = (
  updateKYCDocumentStatus: any,
  user: any,
  setShowLoader: (value: boolean) => void,
  getUserDetails: () => void,
  dispatch: ThunkDispatch<any, any, any>
) => {
  if (updateKYCDocumentStatus && user) {
    if (updateKYCDocumentStatus.errorMessage) {
      setShowLoader(false);
      toast.error(updateKYCDocumentStatus.errorMessage);
      getUserDetails();
    } else if (updateKYCDocumentStatus.successMessage) {
      setShowLoader(false);
      toast.success(updateKYCDocumentStatus.successMessage);
      getUserDetails();
    }
    dispatch(resetUpdateKYCDocumentStatus());
  }
};
